import {MouseEvent, useCallback, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";

const LENS_SIZE_DESKTOP = 300;
const HOVER_MEDIA = "(hover: hover) and (pointer: fine)";

interface ImageMagnifierProps {
    src: string;
    zoomSrc: string;
    alt: string;
    enabled?: boolean;
}

interface LensState {
    left: number;
    top: number;
    size: number;
    bgX: number;
    bgY: number;
    bgW: number;
    bgH: number;
}

function getImageContentRect(img: HTMLImageElement) {
    const rect = img.getBoundingClientRect();
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    if (!naturalWidth || !naturalHeight) {
        return {left: rect.left, top: rect.top, width: rect.width, height: rect.height};
    }

    const scale = Math.min(rect.width / naturalWidth, rect.height / naturalHeight);
    const width = naturalWidth * scale;
    const height = naturalHeight * scale;

    return {
        left: rect.left + (rect.width - width) / 2,
        top: rect.top + (rect.height - height) / 2,
        width,
        height,
    };
}

export default function ImageMagnifier({src, zoomSrc, alt, enabled = true}: ImageMagnifierProps) {
    const imgRef = useRef<HTMLImageElement>(null);
    const zoomSizeRef = useRef<{w: number; h: number} | null>(null);
    const [lens, setLens] = useState<LensState | null>(null);
    const [canHover, setCanHover] = useState(false);

    useEffect(() => {
        const media = window.matchMedia(HOVER_MEDIA);
        const update = () => setCanHover(media.matches);
        update();
        media.addEventListener("change", update);
        return () => media.removeEventListener("change", update);
    }, []);

    useEffect(() => {
        zoomSizeRef.current = null;
        const image = new Image();
        image.src = zoomSrc;
        image.onload = () => {
            zoomSizeRef.current = {w: image.naturalWidth, h: image.naturalHeight};
        };
    }, [zoomSrc]);

    const hideLens = useCallback(() => setLens(null), []);

    useEffect(() => {
        if (!enabled) {
            hideLens();
        }
    }, [enabled, hideLens]);

    const handleMouseMove = useCallback((event: MouseEvent<HTMLImageElement>) => {
        if (!enabled || !canHover) {
            return;
        }

        const img = imgRef.current;
        const zoomSize = zoomSizeRef.current;
        if (!img || !zoomSize) {
            return;
        }

        const content = getImageContentRect(img);
        const x = event.clientX - content.left;
        const y = event.clientY - content.top;

        if (x < 0 || y < 0 || x > content.width || y > content.height) {
            setLens(null);
            return;
        }

        const size = Math.min(LENS_SIZE_DESKTOP, Math.round(window.innerWidth * 0.35));

        setLens({
            left: event.clientX - size / 2,
            top: event.clientY - size / 2,
            size,
            bgX: size / 2 - x * (zoomSize.w / content.width),
            bgY: size / 2 - y * (zoomSize.h / content.height),
            bgW: zoomSize.w,
            bgH: zoomSize.h,
        });
    }, [canHover, enabled]);

    return (
        <>
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                draggable={false}
                className={`image-magnifier__img${lens ? " image-magnifier__img--zoomable" : ""}`}
                onMouseMove={handleMouseMove}
                onMouseLeave={hideLens}
                onPointerLeave={hideLens}
            />
            {lens && createPortal(
                <div
                    className="image-magnifier-lens"
                    style={{
                        left: lens.left,
                        top: lens.top,
                        width: lens.size,
                        height: lens.size,
                        backgroundImage: `url(${zoomSrc})`,
                        backgroundPosition: `${lens.bgX}px ${lens.bgY}px`,
                        backgroundSize: `${lens.bgW}px ${lens.bgH}px`,
                    }}
                />,
                document.body
            )}
        </>
    );
}
